import React from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  ImportedUserRow,
  importUsersRowsUnique,
  userImportDedupeKey,
  fetchExistingUserKeyToId,
} from '../api/users';
import {
  importCollectionRows,
  importSchoolsRowsUnique,
  updateSchoolsRows,
  importCandidaturesRowsUnique,
  importPostsRowsUnique,
  schoolImportDedupeKey,
  fetchExistingSchoolKeyToId,
  candidatureImportDedupeKey,
  fetchExistingCandidatureKeyToId,
  postImportDedupeKey,
  postImportRowEligible,
  fetchExistingPostsImportIndex,
} from '../api/imports';
import * as XLSX from 'xlsx';

type CsvRecord = Record<string, string | undefined>;
type ImportType =
  | 'users'
  | 'schools'
  | 'posts'
  | 'candidatures'
  | 'activities'
  | 'payments';

type ImportParsedRow = Record<string, unknown>;

/** Ligne école après chargement CSV : doublon base → id Firestore renseigné. */
type SchoolPipelineEntry = {
  source: CsvRecord;
  mapped: ImportParsedRow;
  resolvedSchoolId?: string;
  /** Vrai quand l'id est fourni dans le fichier : l'école sera mise à jour. */
  isUpdate?: boolean;
};

type UserPipelineEntry = {
  source: CsvRecord;
  mapped: ImportParsedRow;
  resolvedUserId?: string;
};

type CandidaturePipelineEntry = {
  source: CsvRecord;
  mapped: ImportParsedRow;
  resolvedCandidatureId?: string;
};

type PostPipelineEntry = {
  source: CsvRecord;
  mapped: ImportParsedRow;
  resolvedPostId?: string;
};

/** Doublons fichier + export : postId CSV non résolu en base → même id pour toutes les lignes avec ce postId ; sinon clé métier. */
function postImportFileMergeKey(row: Record<string, unknown>): string {
  const csvPidRaw = String(row.postId ?? '').trim();
  if (csvPidRaw) return `csv:${csvPidRaw}`;
  return postImportDedupeKey(row);
}

const ACCEPTED_ROLES = new Set([
  'admin',
  'drh',
  'cap',
  'ae',
  'responsible',
  'teacher',
  'cgs',
]);

function normalize(value?: string): string {
  return (value ?? '').trim();
}

/** Fragment utilisable dans la partie locale d’une adresse (sans accents, alphanum + tirets). */
function slugForEmailLocalPart(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

/** Empreinte courte et stable sur tout l’enregistrement (dernier recours si rien d’autre ne distingue la ligne). */
function fingerprintCsvRecord(record: CsvRecord): string {
  const payload = Object.keys(record)
    .sort()
    .map((k) => `${k}=${normalize(record[k])}`)
    .join('|');
  let h = 2166136261;
  for (let i = 0; i < payload.length; i += 1) {
    h ^= payload.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36).padStart(6, '0').slice(0, 8);
}

function csvDirectEmail(record: CsvRecord): string {
  return (
    normalize(record.email) ||
    normalize(record.Email) ||
    normalize(record.mail) ||
    ''
  );
}

/** `prenom.nom@yopmail.com` si prénom + nom valides, sinon chaîne vide (pas d’e-mail synthétique). */
function syntheticUserEmailBase(record: CsvRecord): string {
  const firstName = normalize(record.firstName);
  const lastName = normalize(record.lastName);
  if (!firstName || !lastName) return '';
  const fn = slugForEmailLocalPart(firstName);
  const ln = slugForEmailLocalPart(lastName);
  if (!fn || !ln) return '';
  return `${fn}.${ln}@yopmail.com`;
}

/** Suffixe après `+` pour distinguer deux lignes homonymes (même base synthétique). */
function syntheticEmailDisambiguationTag(record: CsvRecord): string {
  const rowNum =
    normalize(record['N°']) ||
    normalize(record['n°']) ||
    normalize(record.No) ||
    normalize(record.no) ||
    normalize(record.numero) ||
    normalize(record.number) ||
    normalize(record.volontaireId) ||
    normalize(record.volunteerId) ||
    normalize(record.lineId);
  if (rowNum) return `n${slugForEmailLocalPart(rowNum)}`;

  const phoneRaw = normalize(record.phone).replace(/\s/g, '');
  if (phoneRaw) return `p${slugForEmailLocalPart(phoneRaw)}`;

  const geoSlots = [
    normalize(record.region),
    normalize(record.academy),
    normalize(record.cap),
    normalize(record.commune),
    normalize(record.schoolName),
    normalize(record.school),
    normalize(record.village),
    normalize(record.address),
  ].filter(Boolean);
  const geoSlug = geoSlots.map(slugForEmailLocalPart).filter(Boolean).join('-');
  if (geoSlug) return geoSlug.slice(0, 55);

  return fingerprintCsvRecord(record);
}

/**
 * Si plusieurs lignes sans e-mail réel partagent `prenom.nom@yopmail.com`, seules celles-là reçoivent
 * un e-mail différencié (`prenom.nom+tag@yopmail.com`). Les lignes uniques gardent la forme simple.
 * Mutile `record.email` pour les lignes concernées.
 */
function applySyntheticEmailOnlyOnDuplicateHomonyms(rows: CsvRecord[]): void {
  type Slot = { index: number; record: CsvRecord; base: string };
  const byBase = new Map<string, Slot[]>();

  rows.forEach((record, index) => {
    if (csvDirectEmail(record)) return;
    const base = syntheticUserEmailBase(record);
    if (!base) return;
    const key = base.toLowerCase();
    const slot = { index, record, base };
    const arr = byBase.get(key);
    if (arr) arr.push(slot);
    else byBase.set(key, [slot]);
  });

  byBase.forEach((slots) => {
    if (slots.length < 2) return;
    slots.sort((a, b) => a.index - b.index);
    slots.forEach((slot, i) => {
      const at = slot.base.indexOf('@');
      const local = slot.base.slice(0, at);
      const domain = slot.base.slice(at + 1);
      if (i === 0) {
        slot.record.email = slot.base;
        return;
      }
      const tagBase = syntheticEmailDisambiguationTag(slot.record).slice(0, 44);
      const tag = `${tagBase}-g${i}`;
      slot.record.email = `${local}+${tag}@${domain}`;
    });
  });
}

/**
 * E-mail issu du fichier ; sinon e-mail synthétique `prenom.nom@yopmail.com`
 * (les homonymes sans e-mail sont différenciés avant mapping par `applySyntheticEmailOnlyOnDuplicateHomonyms`).
 */
function normalizeEmail(record: CsvRecord): string {
  const direct = csvDirectEmail(record);
  if (direct) return direct;
  return syntheticUserEmailBase(record);
}

function parseDate(value?: string): Date | undefined {
  const v = normalize(value);
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function parseNumber(value?: string): number | undefined {
  const v = normalize(value);
  if (!v) return undefined;
  const n = Number(v.replace(',', '.'));
  return Number.isNaN(n) ? undefined : n;
}

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      out.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  out.push(current);
  return out.map((x) => x.trim());
}

function stripBom(s: string): string {
  return s.replace(/^\uFEFF/, '');
}

function parseCsv(text: string): CsvRecord[] {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const records: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < normalized.length; i += 1) {
    const char = normalized[i];
    const next = normalized[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        // Conserve l'échappement CSV brut ; splitCsvLine le traitera ensuite.
        current += '""';
        i += 1;
      } else {
        inQuotes = !inQuotes;
        // Conserve les délimiteurs de champ pour protéger les virgules internes.
        current += '"';
      }
      continue;
    }

    if (char === '\n' && !inQuotes) {
      if (current.trim().length > 0) {
        records.push(current);
      }
      current = '';
      continue;
    }

    current += char;
  }

  if (current.trim().length > 0) {
    records.push(current);
  }

  if (!records.length) return [];
  const headers = splitCsvLine(records[0]).map((h) => stripBom(h.trim()));

  return records.slice(1).map((line) => {
    const values = splitCsvLine(line);
    const record: CsvRecord = {};
    headers.forEach((header, index) => {
      record[header] = values[index] ?? '';
    });
    return record;
  });
}

function isExcelFile(file: File): boolean {
  const n = file.name.toLowerCase();
  return n.endsWith('.xlsx') || n.endsWith('.xls') || n.endsWith('.xlsm');
}

function cellToImportString(cell: unknown): string {
  if (cell == null || cell === '') return '';
  if (cell instanceof Date && !Number.isNaN(cell.getTime())) {
    return cell.toISOString();
  }
  if (typeof cell === 'number' && Number.isFinite(cell)) {
    return String(cell);
  }
  if (typeof cell === 'boolean') {
    return cell ? 'true' : 'false';
  }
  return stripBom(String(cell).trim());
}

/** Première feuille : ligne 1 = en-têtes (identique au CSV). */
function parseExcelToRecords(data: ArrayBuffer): CsvRecord[] {
  const workbook = XLSX.read(data, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName];
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: '',
    blankrows: false,
  });
  if (!aoa.length) return [];
  const headerCells = aoa[0] as unknown[];
  const headers = headerCells.map((h) => cellToImportString(h));
  const out: CsvRecord[] = [];
  for (let ri = 1; ri < aoa.length; ri += 1) {
    const line = (aoa[ri] as unknown[]) || [];
    if (!line.some((c, ci) => ci < headers.length && cellToImportString(c) !== '')) continue;
    const record: CsvRecord = {};
    headers.forEach((header, index) => {
      const key = stripBom(header.trim());
      if (!key) return;
      record[key] = cellToImportString(line[index]);
    });
    out.push(record);
  }
  return out;
}

function mapCsvRecord(record: CsvRecord): ImportedUserRow | null {
  const authUid = normalize(record.authUid);
  const firstName = normalize(record.firstName);
  const lastName = normalize(record.lastName);
  const role = normalize(record.role)?.toLowerCase() || 'teacher';
  const status = normalize(record.status)?.toLowerCase() || 'active';
  const email = normalizeEmail(record);
  const phone = normalize(record.phone);
  const academy = normalize(record.academy);
  const cap = normalize(record.cap);
  const region = normalize(record.region);
  const commune = normalize(record.commune);
  const speciality = normalize(record.speciality);

  if (!firstName || !lastName) return null;
  if (!ACCEPTED_ROLES.has(role)) return null;
  if (status !== 'active' && status !== 'inactive') return null;

  return {
    authUid: authUid || undefined,
    firstName,
    lastName,
    email: email || undefined,
    phone: phone || undefined,
    academy: academy || undefined,
    cap: cap || undefined,
    region: region || undefined,
    commune: commune || undefined,
    speciality: speciality || undefined,
    role: role as ImportedUserRow['role'],
    status: status as ImportedUserRow['status'],
  };
}

/** URLs PDF : une cellule avec `|`, ou une colonne multiligne ; aligné sur `CandidatureModel.documents`. */
function parseCandidatureDocumentsField(raw: unknown): string[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw
      .map((e) => (typeof e === 'string' ? e.trim() : ''))
      .filter(Boolean);
  }
  const s = String(raw).trim();
  if (!s) return [];
  const lines = s.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
  if (lines.length === 1 && lines[0].includes('|')) {
    return lines[0]
      .split('|')
      .map((x) => x.trim())
      .filter(Boolean);
  }
  return lines;
}

function mapRecordByType(type: ImportType, record: CsvRecord): ImportParsedRow | null {
  switch (type) {
    case 'users': {
      const user = mapCsvRecord(record);
      return user ? { ...user } : null;
    }
    case 'schools': {
      const name = normalize(record.name) || normalize(record.schoolName) || normalize(record.school);
      const status = normalize(record.status)?.toLowerCase() || 'active';
      if (!name) return null;
      if (status !== 'active' && status !== 'inactive') return null;
      return {
        name,
        academy: normalize(record.academy) || null,
        cap: normalize(record.cap) || null,
        region: normalize(record.region) || null,
        commune: normalize(record.commune) || null,
        address: normalize(record.address) || normalize(record.region) || null,
        status,
      };
    }
    case 'posts': {
      const mapping = {'active': 'pending'}
      const title = normalize(record.title || ('Poste pour ' + (record.speciality || 'Sans spécialité')));
      const schoolId = normalize(record.schoolId);
      const postId = normalize(record.postId);
      const status = normalize(mapping[record.status as keyof typeof mapping] ?? 'approved').toLowerCase();
      const date = parseDate(record.date) || new Date();
      const summary = normalize(record.summary);
      const description = normalize(record.description);
      if (!title || !schoolId) return null;
      if (!['pending', 'approved', 'rejected'].includes(status)) return null;
      return {
        title,
        schoolId,
        status,
        date,
        ...(summary ? { summary } : {}),
        ...(description ? { description } : {}),
        ...(postId ? { postId } : {}),
      };
    }
    case 'candidatures': {
      const userId = normalize(record.userId);
      const postId = normalize(record.postId);
      const status = normalize(record.status || 'approved').toLowerCase();
      const date = parseDate(record.date) || new Date();
      if (!userId || !postId || !date) return null;
      if (!['pending', 'approved', 'rejected'].includes(status)) return null;
      const description = normalize(record.description) || null;
      const documents = parseCandidatureDocumentsField(
        record.documents ?? record.documentUrls ?? record.document_urls
      );
      return {
        userId,
        postId,
        status,
        date,
        ...(description ? { description } : {}),
        ...(documents.length ? { documents } : {}),
      };
    }
    case 'activities': {
      const candidatureId = normalize(record.candidatureId);
      const status = normalize(record.status || 'pending').toLowerCase();
      const date = parseDate(record.date) || new Date();
      const durationValue = parseNumber(record.durationValue) ?? parseNumber(record.hours);
      const durationUnitRaw = normalize(record.durationUnit || 'hours').toLowerCase();
      const durationUnit =
        durationUnitRaw === 'days' || durationUnitRaw === 'months' || durationUnitRaw === 'hours'
          ? durationUnitRaw
          : '';
      if (!candidatureId || durationValue == null || !durationUnit) return null;
      if (!['pending', 'validated', 'rejected'].includes(status)) return null;
      return {
        candidatureId,
        status,
        date,
        durationValue,
        durationUnit,
        hours: durationUnit === 'hours' ? durationValue : null,
        description: normalize(record.description) || null,
      };
    }
    case 'payments': {
      const activityId = normalize(record.activityId);
      const paidById = normalize(record.paidById);
      const method = normalize(record.method);
      const amount = parseNumber(record.amount);
      const status = normalize(record.status).toLowerCase();
      if (!activityId || !paidById || !method || amount == null) return null;
      if (!['pending', 'paid'].includes(status)) return null;
      return { activityId, paidById, method, amount, status };
    }
    default:
      return null;
  }
}

const FORMAT_HINTS: Record<ImportType, string> = {
  users:
    'authUid,firstName,lastName,email,phone,academy,cap,region,commune,speciality,role,status',
  schools:
    'name,academy,cap,region,commune,address,status[,schoolId] (schoolId présent → mise à jour)',
  posts:
    'title,schoolId,status[,date][,postId][,summary][,description] (date défaut: aujourd’hui)',
  candidatures:
    'userId,postId,status,date[,description][,documents] (documents : URLs séparées par | ou une par ligne)',
  activities: 'candidatureId,status,date,durationValue,durationUnit[,description] (durationUnit: hours|days|months)',
  payments: 'activityId,paidById,method,amount,status',
};

const COLLECTION_BY_TYPE: Record<Exclude<ImportType, 'users'>, string> = {
  schools: 'schools',
  posts: 'posts',
  candidatures: 'candidatures',
  activities: 'activities',
  payments: 'payments',
};

const ID_COLUMN_BY_TYPE: Record<ImportType, string> = {
  users: 'userId',
  schools: 'schoolId',
  posts: 'postId',
  candidatures: 'candidatureId',
  activities: 'activityId',
  payments: 'paymentId',
};

function csvEscape(value: unknown): string {
  const str = String(value ?? '');
  if (str.includes('"') || str.includes(',') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function rowsToCsv(rows: Array<Record<string, unknown>>): string {
  if (!rows.length) return '';
  const headers = Object.keys(rows[0]);
  const headerLine = headers.map(csvEscape).join(',');
  const body = rows.map((row) => headers.map((h) => csvEscape(row[h])).join(','));
  return [headerLine, ...body].join('\n');
}

function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const ImportsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [importType, setImportType] = React.useState<ImportType>('users');
  const [rows, setRows] = React.useState<ImportParsedRow[]>([]);
  const [sourceRows, setSourceRows] = React.useState<CsvRecord[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [importing, setImporting] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [loadInfo, setLoadInfo] = React.useState<string | null>(null);
  const [pipelineFetchPending, setPipelineFetchPending] = React.useState(false);
  /** Toutes les écoles du fichier (ordre), avec `schoolId` Firestore si déjà en base. */
  const [schoolPipeline, setSchoolPipeline] = React.useState<SchoolPipelineEntry[] | null>(null);
  const [userPipeline, setUserPipeline] = React.useState<UserPipelineEntry[] | null>(null);
  const [candidaturePipeline, setCandidaturePipeline] = React.useState<
    CandidaturePipelineEntry[] | null
  >(null);
  const [postPipeline, setPostPipeline] = React.useState<PostPipelineEntry[] | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setSuccessMessage(null);
    setLoadInfo(null);
    setSchoolPipeline(null);
    setUserPipeline(null);
    setCandidaturePipeline(null);
    setPostPipeline(null);
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      void (async () => {
        try {
          const buf = reader.result;
          if (!(buf instanceof ArrayBuffer)) {
            setRows([]);
            setSourceRows([]);
            setError('Impossible de lire le fichier (format inattendu).');
            return;
          }
          const parsedRows = isExcelFile(file)
            ? parseExcelToRecords(buf)
            : parseCsv(new TextDecoder('utf-8', { fatal: false }).decode(buf));

          if (importType === 'users') {
            applySyntheticEmailOnlyOnDuplicateHomonyms(parsedRows);
            setPipelineFetchPending(true);
            try {
              const candidates: { mapped: ImportParsedRow; source: CsvRecord }[] = [];
              parsedRows.forEach((r) => {
                const mapped = mapRecordByType('users', r);
                if (mapped) candidates.push({ mapped, source: r });
              });

              const seenInFile = new Set<string>();
              let skippedInFile = 0;
              const afterFile: { mapped: ImportParsedRow; source: CsvRecord }[] = [];
              candidates.forEach(({ mapped, source }) => {
                const row = mapped as Record<string, unknown>;
                const key = userImportDedupeKey(row);
                if (!String(row.firstName ?? '').trim() || !String(row.lastName ?? '').trim()) return;
                if (seenInFile.has(key)) {
                  skippedInFile += 1;
                  return;
                }
                seenInFile.add(key);
                afterFile.push({ mapped, source });
              });

              const existingKeyToId = await fetchExistingUserKeyToId();
              const pipeline: UserPipelineEntry[] = [];
              let linkedFromDb = 0;
              afterFile.forEach(({ mapped, source }) => {
                const key = userImportDedupeKey(mapped);
                const existingId = existingKeyToId.get(key);
                if (existingId) {
                  linkedFromDb += 1;
                  const mappedWithId = { ...mapped, userId: existingId } as ImportParsedRow;
                  pipeline.push({ source, mapped: mappedWithId, resolvedUserId: existingId });
                } else {
                  pipeline.push({ source, mapped });
                }
              });

              const toCreate = pipeline.filter((e) => !e.resolvedUserId);
              setUserPipeline(pipeline);

              if (!pipeline.length) {
                setRows([]);
                setSourceRows([]);
                setUserPipeline(null);
                setError(
                  `Aucun utilisateur valide après dédoublonnage dans le fichier. Colonnes attendues: ${FORMAT_HINTS.users}`
                );
                if (skippedInFile) setLoadInfo(`${skippedInFile} doublon(s) dans le fichier`);
                return;
              }

              const parts: string[] = [];
              if (skippedInFile) parts.push(`${skippedInFile} doublon(s) dans le fichier`);
              if (linkedFromDb) {
                parts.push(
                  `${linkedFromDb} déjà en base (userId renseigné localement et dans l’export CSV)`
                );
              }
              if (parts.length) setLoadInfo(parts.join(' · '));

              setRows(toCreate.map((e) => e.mapped));
              setSourceRows(toCreate.map((e) => e.source));
              setError(null);
              return;
            } finally {
              setPipelineFetchPending(false);
            }
          }

          if (importType === 'candidatures') {
            setPipelineFetchPending(true);
            try {
              const candidates: { mapped: ImportParsedRow; source: CsvRecord }[] = [];
              parsedRows.forEach((r) => {
                const mapped = mapRecordByType('candidatures', r);
                if (mapped) candidates.push({ mapped, source: r });
              });

              const seenInFile = new Set<string>();
              let skippedInFile = 0;
              const afterFile: { mapped: ImportParsedRow; source: CsvRecord }[] = [];
              candidates.forEach(({ mapped, source }) => {
                const row = mapped as Record<string, unknown>;
                const key = candidatureImportDedupeKey(row);
                if (!String(row.userId ?? '').trim() || !String(row.postId ?? '').trim()) return;
                if (seenInFile.has(key)) {
                  skippedInFile += 1;
                  return;
                }
                seenInFile.add(key);
                afterFile.push({ mapped, source });
              });

              const existingKeyToId = await fetchExistingCandidatureKeyToId();
              const pipeline: CandidaturePipelineEntry[] = [];
              let linkedFromDb = 0;
              afterFile.forEach(({ mapped, source }) => {
                const key = candidatureImportDedupeKey(mapped as Record<string, unknown>);
                const existingId = existingKeyToId.get(key);
                if (existingId) {
                  linkedFromDb += 1;
                  const mappedWithId = {
                    ...mapped,
                    candidatureId: existingId,
                  } as ImportParsedRow;
                  pipeline.push({
                    source,
                    mapped: mappedWithId,
                    resolvedCandidatureId: existingId,
                  });
                } else {
                  pipeline.push({ source, mapped });
                }
              });

              const toCreate = pipeline.filter((e) => !e.resolvedCandidatureId);
              setCandidaturePipeline(pipeline);

              if (!pipeline.length) {
                setRows([]);
                setSourceRows([]);
                setCandidaturePipeline(null);
                setError(
                  `Aucune candidature valide après dédoublonnage dans le fichier. Colonnes attendues: ${FORMAT_HINTS.candidatures}`
                );
                if (skippedInFile) setLoadInfo(`${skippedInFile} doublon(s) dans le fichier`);
                return;
              }

              const parts: string[] = [];
              if (skippedInFile) parts.push(`${skippedInFile} doublon(s) dans le fichier`);
              if (linkedFromDb) {
                parts.push(
                  `${linkedFromDb} déjà en base (candidatureId renseigné localement et dans l’export CSV)`
                );
              }
              if (parts.length) setLoadInfo(parts.join(' · '));

              setRows(toCreate.map((e) => e.mapped));
              setSourceRows(toCreate.map((e) => e.source));
              setError(null);
              return;
            } finally {
              setPipelineFetchPending(false);
            }
          }

          if (importType === 'posts') {
            setPipelineFetchPending(true);
            try {
              const candidates: { mapped: ImportParsedRow; source: CsvRecord }[] = [];
              parsedRows.forEach((r) => {
                const mapped = mapRecordByType('posts', r);
                if (mapped) candidates.push({ mapped, source: r });
              });

              const { keyToId: existingKeyToId, existingIds } = await fetchExistingPostsImportIndex();

              const eligible = candidates.filter((c) =>
                postImportRowEligible(c.mapped as Record<string, unknown>)
              );

              /** Toutes les lignes éligibles conservées pour l’export CSV ; une seule création Firestore par clé (postId CSV orphelin ou clé métier). */
              const pipeline: PostPipelineEntry[] = [];
              let linkedFromDb = 0;
              eligible.forEach(({ mapped, source }) => {
                const row = mapped as Record<string, unknown>;
                const csvPidRaw = String(row.postId ?? '').trim();
                if (csvPidRaw && existingIds.has(csvPidRaw)) {
                  linkedFromDb += 1;
                  pipeline.push({
                    source,
                    mapped: { ...mapped, postId: csvPidRaw } as ImportParsedRow,
                    resolvedPostId: csvPidRaw,
                  });
                  return;
                }
                const key = postImportDedupeKey(row);
                const existingId = existingKeyToId.get(key);
                if (existingId) {
                  linkedFromDb += 1;
                  const mappedWithId = { ...mapped, postId: existingId } as ImportParsedRow;
                  pipeline.push({
                    source,
                    mapped: mappedWithId,
                    resolvedPostId: existingId,
                  });
                } else {
                  pipeline.push({ source, mapped });
                }
              });

              const seenCreateKey = new Set<string>();
              const uniqueCreate: { mapped: ImportParsedRow; source: CsvRecord }[] = [];
              pipeline.forEach((e) => {
                if (e.resolvedPostId) return;
                const row = e.mapped as Record<string, unknown>;
                const ck = postImportFileMergeKey(row);
                if (seenCreateKey.has(ck)) return;
                seenCreateKey.add(ck);
                const csvPidRaw = String(row.postId ?? '').trim();
                let mappedForApi: ImportParsedRow;
                if (csvPidRaw) {
                  const { postId: _omit, ...rest } = row;
                  mappedForApi = rest as ImportParsedRow;
                } else {
                  mappedForApi = e.mapped;
                }
                uniqueCreate.push({ mapped: mappedForApi, source: e.source });
              });

              setPostPipeline(pipeline);

              if (!pipeline.length) {
                setRows([]);
                setSourceRows([]);
                setPostPipeline(null);
                setError(
                  `Aucun poste valide dans le fichier. Colonnes attendues: ${FORMAT_HINTS.posts}`
                );
                return;
              }

              const distinctMergeKeys = new Set(
                pipeline.map((e) =>
                  e.resolvedPostId
                    ? `r:${e.resolvedPostId}`
                    : postImportFileMergeKey(e.mapped as Record<string, unknown>)
                )
              );
              const duplicateLineCount = pipeline.length - distinctMergeKeys.size;

              const parts: string[] = [];
              if (duplicateLineCount > 0) {
                parts.push(
                  `${duplicateLineCount} ligne(s) en doublon (même postId CSV ou même école + titre + jour) — même postId sur l’export`
                );
              }
              if (linkedFromDb) {
                parts.push(
                  `${linkedFromDb} ligne(s) déjà en base (postId renseigné localement et dans l’export CSV)`
                );
              }
              if (parts.length) setLoadInfo(parts.join(' · '));

              setRows(uniqueCreate.map((e) => e.mapped));
              setSourceRows(uniqueCreate.map((e) => e.source));
              setError(null);
              return;
            } finally {
              setPipelineFetchPending(false);
            }
          }

          if (importType === 'schools') {
            setPipelineFetchPending(true);
            try {
            const candidates: { mapped: ImportParsedRow; source: CsvRecord }[] = [];
            parsedRows.forEach((r) => {
              const mapped = mapRecordByType('schools', r);
              if (mapped) candidates.push({ mapped, source: r });
            });

            const existingKeyToId = await fetchExistingSchoolKeyToId();
            const pipeline: SchoolPipelineEntry[] = [];
            let linkedFromDb = 0;
            let updateFromId = 0;
            candidates.forEach(({ mapped, source }) => {
              const row = mapped as Record<string, unknown>;
              if (!String(row.name ?? '').trim()) return;
              const explicitId =
                normalize(source.schoolId) || normalize(source.id);
              if (explicitId) {
                // Id présent dans le fichier → mise à jour de l'école existante.
                updateFromId += 1;
                const mappedWithId = { ...mapped, schoolId: explicitId } as ImportParsedRow;
                pipeline.push({
                  source,
                  mapped: mappedWithId,
                  resolvedSchoolId: explicitId,
                  isUpdate: true,
                });
                return;
              }
              const key = schoolImportDedupeKey(row);
              const existingId = existingKeyToId.get(key);
              if (existingId) {
                linkedFromDb += 1;
                const mappedWithId = { ...mapped, schoolId: existingId } as ImportParsedRow;
                pipeline.push({
                  source,
                  mapped: mappedWithId,
                  resolvedSchoolId: existingId,
                });
              } else {
                pipeline.push({ source, mapped });
              }
            });

            /** Une seule création Firestore par clé métier ; toutes les lignes du fichier restent dans le pipeline pour l’export CSV. */
            const seenCreateKey = new Set<string>();
            const uniqueCreate: { mapped: ImportParsedRow; source: CsvRecord }[] = [];
            pipeline.forEach((e) => {
              if (e.resolvedSchoolId) return;
              const k = schoolImportDedupeKey(e.mapped as Record<string, unknown>);
              if (seenCreateKey.has(k)) return;
              seenCreateKey.add(k);
              uniqueCreate.push({ mapped: e.mapped, source: e.source });
            });

            setSchoolPipeline(pipeline);

            if (!pipeline.length) {
              setRows([]);
              setSourceRows([]);
              setSchoolPipeline(null);
              setError(
                `Aucune école valide dans le fichier. Colonnes attendues: ${FORMAT_HINTS.schools}`
              );
              return;
            }

            const distinctKeys = new Set(
              pipeline.map((e) => schoolImportDedupeKey(e.mapped as Record<string, unknown>))
            );
            const duplicateLineCount = pipeline.length - distinctKeys.size;

            const parts: string[] = [];
            if (duplicateLineCount > 0) {
              parts.push(
                `${duplicateLineCount} ligne(s) en doublon (même nom + commune) — même schoolId sur l’export`
              );
            }
            if (updateFromId) {
              parts.push(
                `${updateFromId} ligne(s) avec id → mise à jour de l’école existante`
              );
            }
            if (linkedFromDb) {
              parts.push(
                `${linkedFromDb} ligne(s) déjà en base (schoolId renseigné localement et dans l’export CSV)`
              );
            }
            if (parts.length) {
              setLoadInfo(parts.join(' · '));
            }

            setRows(uniqueCreate.map((e) => e.mapped));
            setSourceRows(uniqueCreate.map((e) => e.source));
            setError(null);
            return;
            } finally {
              setPipelineFetchPending(false);
            }
          }

          const validRows: ImportParsedRow[] = [];
          const validSourceRows: CsvRecord[] = [];
          parsedRows.forEach((r) => {
            const mapped = mapRecordByType(importType, r);
            if (mapped) {
              validRows.push(mapped);
              validSourceRows.push(r);
            }
          });

          if (!validRows.length) {
            setRows([]);
            setSourceRows([]);
            setError(
              `Aucune ligne valide trouvée. Colonnes attendues: ${FORMAT_HINTS[importType]}`
            );
            return;
          }

          setRows(validRows);
          setSourceRows(validSourceRows);
        } catch {
          setRows([]);
          setSourceRows([]);
          setSchoolPipeline(null);
          setUserPipeline(null);
          setCandidaturePipeline(null);
          setPostPipeline(null);
          setPipelineFetchPending(false);
          setLoadInfo(null);
          setError('Impossible de lire le fichier CSV ou Excel.');
        }
      })();
    };
    reader.onerror = () => {
      setRows([]);
      setSourceRows([]);
      setSchoolPipeline(null);
      setUserPipeline(null);
      setCandidaturePipeline(null);
      setPostPipeline(null);
      setError('Impossible de lire le fichier CSV ou Excel.');
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (importType === 'schools') {
      if (!schoolPipeline?.length) return;
    } else if (importType === 'users') {
      if (!userPipeline?.length) return;
    } else if (importType === 'candidatures') {
      if (!candidaturePipeline?.length) return;
    } else if (importType === 'posts') {
      if (!postPipeline?.length) return;
    } else if (!rows.length) {
      return;
    }
    setImporting(true);
    setError(null);
    setSuccessMessage(null);
    setLoadInfo(null);
    try {
      let createdIds: string[] = [];
      let skippedCount = 0;
      let updatedCount = 0;
      if (importType === 'users') {
        if (rows.length > 0) {
          const result = await importUsersRowsUnique(rows as unknown as ImportedUserRow[]);
          createdIds = result.idsByRow;
          skippedCount = result.skippedCount;
        } else {
          createdIds = [];
          skippedCount = 0;
        }
        await queryClient.invalidateQueries({ queryKey: ['users'] });
      } else if (importType === 'schools') {
        if (rows.length > 0) {
          const result = await importSchoolsRowsUnique(rows);
          createdIds = result.idsByRow;
          skippedCount = result.skippedCount;
        } else {
          createdIds = [];
          skippedCount = 0;
        }
        // Mise à jour des écoles dont l'id est présent dans le fichier.
        const updates = (schoolPipeline ?? [])
          .filter((e) => e.isUpdate && e.resolvedSchoolId)
          .map((e) => {
            const { schoolId: _omit, ...payload } =
              e.mapped as Record<string, unknown>;
            void _omit;
            return { id: e.resolvedSchoolId as string, payload };
          });
        updatedCount = await updateSchoolsRows(updates);
        await queryClient.invalidateQueries({ queryKey: ['schools'] });
      } else if (importType === 'candidatures') {
        if (rows.length > 0) {
          const result = await importCandidaturesRowsUnique(rows);
          createdIds = result.idsByRow;
          skippedCount = result.skippedCount;
        } else {
          createdIds = [];
          skippedCount = 0;
        }
        await queryClient.invalidateQueries({ queryKey: ['candidatures'] });
      } else if (importType === 'posts') {
        if (rows.length > 0) {
          const result = await importPostsRowsUnique(rows);
          createdIds = result.idsByRow;
          skippedCount = result.skippedCount;
        } else {
          createdIds = [];
          skippedCount = 0;
        }
        await queryClient.invalidateQueries({ queryKey: ['posts'] });
      } else {
        const collectionName = COLLECTION_BY_TYPE[importType];
        createdIds = await importCollectionRows(collectionName, rows);
        await queryClient.invalidateQueries({ queryKey: [collectionName] });
      }

      const idColumn = ID_COLUMN_BY_TYPE[importType];
      let merged: Array<Record<string, unknown>>;
      if (importType === 'schools' && schoolPipeline?.length) {
        const createdKeyToId = new Map<string, string>();
        rows.forEach((row, i) => {
          const id = createdIds[i];
          if (!id) return;
          const k = schoolImportDedupeKey(row as Record<string, unknown>);
          createdKeyToId.set(k, id);
        });
        merged = schoolPipeline.map((entry) => {
          const key = schoolImportDedupeKey(entry.mapped as Record<string, unknown>);
          const id =
            entry.resolvedSchoolId ?? createdKeyToId.get(key) ?? '';
          return {
            ...entry.source,
            ...entry.mapped,
            [idColumn]: id,
          };
        });
      } else if (importType === 'users' && userPipeline?.length) {
        let createIdx = 0;
        merged = userPipeline.map((entry) => ({
          ...entry.source,
          ...entry.mapped,
          [idColumn]:
            entry.resolvedUserId ?? (createdIds[createIdx++] as string | undefined) ?? '',
        }));
      } else if (importType === 'candidatures' && candidaturePipeline?.length) {
        let createIdx = 0;
        merged = candidaturePipeline.map((entry) => ({
          ...entry.source,
          ...entry.mapped,
          [idColumn]:
            entry.resolvedCandidatureId ??
            (createdIds[createIdx++] as string | undefined) ??
            '',
        }));
      } else if (importType === 'posts' && postPipeline?.length) {
        const createdKeyToId = new Map<string, string>();
        const seenCreate = new Set<string>();
        let createIdx = 0;
        postPipeline.forEach((e) => {
          if (e.resolvedPostId) return;
          const ck = postImportFileMergeKey(e.mapped as Record<string, unknown>);
          if (seenCreate.has(ck)) return;
          seenCreate.add(ck);
          const id = createdIds[createIdx++] as string | undefined;
          if (id) createdKeyToId.set(ck, id);
        });
        merged = postPipeline.map((entry) => {
          const row = entry.mapped as Record<string, unknown>;
          const ck = postImportFileMergeKey(row);
          const id = entry.resolvedPostId ?? createdKeyToId.get(ck) ?? '';
          return {
            ...entry.source,
            ...entry.mapped,
            [idColumn]: id,
          };
        });
      } else {
        merged = rows.map((row, index) => ({
          ...(sourceRows[index] ?? {}),
          ...row,
          [idColumn]: createdIds[index] ?? '',
        }));
      }
      const csv = rowsToCsv(merged);
      if (csv) {
        const filename = `${importType}-import-result-${Date.now()}.csv`;
        downloadCsv(filename, csv);
      }

      const importedCount = createdIds.filter(Boolean).length;
      if (importType === 'schools' && schoolPipeline?.length) {
        const linked = schoolPipeline.filter(
          (e) => e.resolvedSchoolId && !e.isUpdate
        ).length;
        const segments: string[] = [];
        if (importedCount > 0) segments.push(`${importedCount} créée(s)`);
        if (updatedCount > 0) segments.push(`${updatedCount} mise(s) à jour`);
        if (linked > 0) segments.push(`${linked} déjà en base`);
        setSuccessMessage(
          segments.length
            ? `Écoles : ${segments.join(', ')}.`
            : 'Import terminé.'
        );
      } else if (importType === 'users' && userPipeline?.length) {
        const linked = userPipeline.filter((e) => e.resolvedUserId).length;
        if (importedCount > 0 && linked > 0) {
          setSuccessMessage(
            `${importedCount} utilisateur(s) créé(s), ${linked} déjà en base (userId dans le CSV).`
          );
        } else if (importedCount > 0) {
          setSuccessMessage(`${importedCount} utilisateur(s) importé(s) avec succès.`);
        } else if (linked > 0) {
          setSuccessMessage(
            `${linked} utilisateur(s) déjà en base : CSV mis à jour avec les userId (aucune création).`
          );
        } else {
          setSuccessMessage('Import terminé.');
        }
      } else if (importType === 'candidatures' && candidaturePipeline?.length) {
        const linked = candidaturePipeline.filter((e) => e.resolvedCandidatureId).length;
        if (importedCount > 0 && linked > 0) {
          setSuccessMessage(
            `${importedCount} candidature(s) créée(s), ${linked} déjà en base (candidatureId dans le CSV).`
          );
        } else if (importedCount > 0) {
          setSuccessMessage(`${importedCount} candidature(s) importée(s) avec succès.`);
        } else if (linked > 0) {
          setSuccessMessage(
            `${linked} candidature(s) déjà en base : CSV mis à jour avec les candidatureId (aucune création).`
          );
        } else {
          setSuccessMessage('Import terminé.');
        }
      } else if (importType === 'posts' && postPipeline?.length) {
        const linked = postPipeline.filter((e) => e.resolvedPostId).length;
        if (importedCount > 0 && linked > 0) {
          setSuccessMessage(
            `${importedCount} poste(s) créé(s), ${linked} ligne(s) déjà en base (postId dans le CSV).`
          );
        } else if (importedCount > 0) {
          setSuccessMessage(`${importedCount} poste(s) importé(s) avec succès.`);
        } else if (linked > 0) {
          setSuccessMessage(
            `${linked} ligne(s) déjà en base : CSV mis à jour avec les postId (aucune création).`
          );
        } else {
          setSuccessMessage('Import terminé.');
        }
      } else if (skippedCount > 0) {
        setSuccessMessage(
          `${importedCount} ligne(s) importée(s), ${skippedCount} doublon(s) ignoré(s).`
        );
      } else {
        setSuccessMessage(`${importedCount} ligne(s) importée(s) avec succès.`);
      }
      setRows([]);
      setSourceRows([]);
      setSchoolPipeline(null);
      setUserPipeline(null);
      setCandidaturePipeline(null);
      setPostPipeline(null);
    } catch(e) {
      console.error(e);
      setError(`L'import a échoué. Vérifiez le format du fichier. ${e}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Import de données (CSV ou Excel)
      </Typography>
      <FormControl size="small" sx={{ minWidth: 280, mb: 2 }}>
        <InputLabel id="import-type-label">Type de données</InputLabel>
        <Select
          labelId="import-type-label"
          label="Type de données"
          value={importType}
          onChange={(e) => {
            setImportType(e.target.value as ImportType);
            setRows([]);
            setSourceRows([]);
            setSchoolPipeline(null);
            setUserPipeline(null);
            setCandidaturePipeline(null);
            setPostPipeline(null);
            setError(null);
            setSuccessMessage(null);
            setLoadInfo(null);
            setPipelineFetchPending(false);
          }}
        >
          <MenuItem value="users">Utilisateurs</MenuItem>
          <MenuItem value="schools">Écoles</MenuItem>
          <MenuItem value="posts">Postes</MenuItem>
          <MenuItem value="candidatures">Candidatures</MenuItem>
          <MenuItem value="activities">Activités</MenuItem>
          <MenuItem value="payments">Paiements</MenuItem>
        </Select>
      </FormControl>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Format attendu: <code>{FORMAT_HINTS[importType]}</code>
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Button variant="outlined" component="label">
          Choisir un fichier (CSV ou Excel)
          <input
            type="file"
            hidden
            accept=".csv,.xlsx,.xls,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={handleFileChange}
          />
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={
            importing ||
            (importType === 'schools'
              ? !schoolPipeline?.length
              : importType === 'users'
                ? !userPipeline?.length
                : importType === 'candidatures'
                  ? !candidaturePipeline?.length
                  : importType === 'posts'
                    ? !postPipeline?.length
                    : !rows.length)
          }
        >
          {importing
            ? 'Import en cours...'
            : importType === 'schools' && schoolPipeline?.length
              ? schoolPipeline.some((e) => e.isUpdate)
                ? rows.length
                  ? 'Importer / Mettre à jour'
                  : 'Mettre à jour'
                : rows.length
                  ? 'Importer'
                  : 'Générer le CSV (schoolId)'
              : importType === 'users' && !rows.length && userPipeline?.length
                ? 'Générer le CSV (userId)'
                : importType === 'candidatures' && !rows.length && candidaturePipeline?.length
                  ? 'Générer le CSV (candidatureId)'
                  : importType === 'posts' && !rows.length && postPipeline?.length
                    ? 'Générer le CSV (postId)'
                    : 'Importer'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loadInfo && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {loadInfo}
        </Alert>
      )}
      {pipelineFetchPending && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Comparaison avec les données déjà en base…
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {importType === 'schools' && schoolPipeline && schoolPipeline.length > 0 ? (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Prévisualisation ({schoolPipeline.length} ligne(s) du fichier —{' '}
            {schoolPipeline.filter((e) => e.resolvedSchoolId).length} avec schoolId déjà connu en base)
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {(() => {
                    const mk = schoolPipeline[0].mapped as Record<string, unknown>;
                    const keys = Object.keys(mk).filter((k) => k !== 'schoolId');
                    return (
                      <>
                        <TableCell>schoolId</TableCell>
                        {keys.map((key) => (
                          <TableCell key={key}>{key}</TableCell>
                        ))}
                      </>
                    );
                  })()}
                </TableRow>
              </TableHead>
              <TableBody>
                {schoolPipeline.slice(0, 20).map((entry, index) => {
                  const mk = schoolPipeline[0].mapped as Record<string, unknown>;
                  const keys = Object.keys(mk).filter((k) => k !== 'schoolId');
                  const sid =
                    entry.resolvedSchoolId ??
                    (String((entry.mapped as Record<string, unknown>).schoolId ?? '') ||
                      '(nouveau)');
                  return (
                    <TableRow key={`school-${index}`}>
                      <TableCell>{sid}</TableCell>
                      {keys.map((key) => (
                        <TableCell key={key}>
                          {String((entry.mapped as Record<string, unknown>)[key] ?? '—')}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : importType === 'users' && userPipeline && userPipeline.length > 0 ? (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Prévisualisation ({userPipeline.length} utilisateur(s) —{' '}
            {userPipeline.filter((e) => e.resolvedUserId).length} déjà en base avec userId)
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {(() => {
                    const mk = userPipeline[0].mapped as Record<string, unknown>;
                    const keys = Object.keys(mk).filter((k) => k !== 'userId');
                    return (
                      <>
                        <TableCell>userId</TableCell>
                        {keys.map((key) => (
                          <TableCell key={key}>{key}</TableCell>
                        ))}
                      </>
                    );
                  })()}
                </TableRow>
              </TableHead>
              <TableBody>
                {userPipeline.slice(0, 20).map((entry, index) => {
                  const mk = userPipeline[0].mapped as Record<string, unknown>;
                  const keys = Object.keys(mk).filter((k) => k !== 'userId');
                  const uid =
                    entry.resolvedUserId ??
                    (String((entry.mapped as Record<string, unknown>).userId ?? '') || '(nouveau)');
                  return (
                    <TableRow key={`user-${index}`}>
                      <TableCell>{uid}</TableCell>
                      {keys.map((key) => (
                        <TableCell key={key}>
                          {String((entry.mapped as Record<string, unknown>)[key] ?? '—')}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : importType === 'candidatures' && candidaturePipeline && candidaturePipeline.length > 0 ? (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Prévisualisation ({candidaturePipeline.length} candidature(s) —{' '}
            {candidaturePipeline.filter((e) => e.resolvedCandidatureId).length} déjà en base avec
            candidatureId)
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {(() => {
                    const mk = candidaturePipeline[0].mapped as Record<string, unknown>;
                    const keys = Object.keys(mk).filter((k) => k !== 'candidatureId');
                    return (
                      <>
                        <TableCell>candidatureId</TableCell>
                        {keys.map((key) => (
                          <TableCell key={key}>{key}</TableCell>
                        ))}
                      </>
                    );
                  })()}
                </TableRow>
              </TableHead>
              <TableBody>
                {candidaturePipeline.slice(0, 20).map((entry, index) => {
                  const mk = candidaturePipeline[0].mapped as Record<string, unknown>;
                  const keys = Object.keys(mk).filter((k) => k !== 'candidatureId');
                  const cid =
                    entry.resolvedCandidatureId ??
                    (String((entry.mapped as Record<string, unknown>).candidatureId ?? '') ||
                      '(nouveau)');
                  return (
                    <TableRow key={`cand-${index}`}>
                      <TableCell>{cid}</TableCell>
                      {keys.map((key) => (
                        <TableCell key={key}>
                          {String((entry.mapped as Record<string, unknown>)[key] ?? '—')}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : importType === 'posts' && postPipeline && postPipeline.length > 0 ? (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Prévisualisation ({postPipeline.length} ligne(s) du fichier —{' '}
            {postPipeline.filter((e) => e.resolvedPostId).length} avec postId déjà connu en base)
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {(() => {
                    const mk = postPipeline[0].mapped as Record<string, unknown>;
                    const keys = Object.keys(mk).filter((k) => k !== 'postId');
                    return (
                      <>
                        <TableCell>postId</TableCell>
                        {keys.map((key) => (
                          <TableCell key={key}>{key}</TableCell>
                        ))}
                      </>
                    );
                  })()}
                </TableRow>
              </TableHead>
              <TableBody>
                {postPipeline.slice(0, 20).map((entry, index) => {
                  const mk = postPipeline[0].mapped as Record<string, unknown>;
                  const keys = Object.keys(mk).filter((k) => k !== 'postId');
                  const pid =
                    entry.resolvedPostId ??
                    (String((entry.mapped as Record<string, unknown>).postId ?? '') || '(nouveau)');
                  return (
                    <TableRow key={`post-${index}`}>
                      <TableCell>{pid}</TableCell>
                      {keys.map((key) => (
                        <TableCell key={key}>
                          {String((entry.mapped as Record<string, unknown>)[key] ?? '—')}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        rows.length > 0 && (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Prévisualisation ({rows.length} ligne(s) valide(s))
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {Object.keys(rows[0] ?? {}).map((key) => (
                      <TableCell key={key}>{key}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.slice(0, 20).map((row, index) => (
                    <TableRow key={`row-${index}`}>
                      {Object.keys(rows[0] ?? {}).map((key) => (
                        <TableCell key={key}>
                          {String((row as Record<string, unknown>)[key] ?? '—')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )
      )}
    </Paper>
  );
};

